import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useLanguage } from '@/contexts/LanguageContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { BASE_URL, STRIPE_PUBLISHABLE_KEY } from '@/config/api';
import { ShoppingBag, Tag, Ticket, CreditCard, Loader2, CheckCircle, X, Lock } from 'lucide-react';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

interface Product {
  id: string;
  title: string;
  description: string;
  quantity: number;
  price: number;
  totalPrice: number;
  coach: {
    id: string;
    name: string;
  };
}

const ShopPage: React.FC = () => {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [buyButtonLoading, setBuyButtonLoading] = useState<{ [key: string]: boolean }>({});
  const [invoiceId, setInvoiceId] = useState<number | null>(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BASE_URL}/api/shop/products`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }
        const data = await response.json();
        setProducts(data || []);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err instanceof Error ? err.message : 'An unknown error occurred');
        setLoading(false);
      }
    };
    fetchProducts();
  }, [token]);

  const handleBuyNow = async (product: Product) => {
    try {
      setBuyButtonLoading((prev) => ({ ...prev, [product.id]: true }));
      setSelectedProduct(product);
      setPaymentStatus('processing');

      const response = await fetch(`${BASE_URL}/api/create-payment-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          amount: product.price * product.quantity,
          currency: 'EUR',
          productId: product.id,
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to create payment intent');
      }
      const data = await response.json();
      setClientSecret(data.clientSecret);
    } catch (err) {
      console.error('Error creating payment intent:', err);
      setPaymentStatus('error');
      setPaymentError(err instanceof Error ? err.message : 'Failed to process payment');
      toast({
        variant: 'destructive',
        title: t('payment.payment_failed'),
        description: err instanceof Error ? err.message : 'Failed to process payment',
      });
    } finally {
      setBuyButtonLoading((prev) => ({ ...prev, [product.id]: false }));
    }
  };

  useEffect(() => {
    if (invoiceId) {
      navigate('/invoices/' + invoiceId);
    }
  }, [invoiceId]);

  const handlePaymentSuccess = () => {
    setPaymentStatus('success');
    toast({
      title: t('payment.payment_successful'),
      description: t('payment.success_description'),
    });

      setSelectedProduct(null);
      setClientSecret(null);
      setPaymentStatus('idle');
      fetchProducts();
  };

  const handlePaymentError = (errorMessage: string) => {
    setPaymentStatus('error');
    setPaymentError(errorMessage);
    toast({
      variant: 'destructive',
      title: t('payment.payment_failed'),
      description: errorMessage,
    });
  };

  const handleCancelPayment = () => {
    setSelectedProduct(null);
    setClientSecret(null);
    setPaymentStatus('idle');
    setPaymentError(null);
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/shop/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }
      const data = await response.json();
      setProducts(data || []);
    } catch (err) {
      console.error('Error refreshing products:', err);
    }
  };

  const options: StripeElementsOptions | undefined = clientSecret
    ? {
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#8B5CF6',
            colorBackground: '#ffffff',
            colorText: '#1A1F2C',
          },
        },
        paymentMethodCreation: 'manual',
      }
    : undefined;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 container py-8 mt-16">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-4">
              <div className="flex items-center">
                <ShoppingBag className="h-6 w-6 mr-2" />
                <CardTitle>{t('shop.title')}</CardTitle>
              </div>
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2">{t('common.loading')}</span>
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
                  <strong className="font-bold mr-1">Error:</strong>
                  <span className="block sm:inline">{error}</span>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-20">
                  <ShoppingBag className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h2 className="text-xl font-medium text-gray-600 mb-2">{t('shop.noProducts')}</h2>
                  <p className="text-gray-500">{t('shop.yourCoachHasNoProductsYet')}</p>
                </div>
              ) : (
                <>
                  {clientSecret && selectedProduct ? (
                    <div className="max-w-3xl mx-auto w-full">
                      <Card className="w-full">
                        <CardHeader>
                          <div className="flex justify-between items-start">
                            <div>
                              <CardTitle>{t('payment.title')}</CardTitle>
                              <CardDescription>{t('payment.card_details')}</CardDescription>
                            </div>
                            <Button variant="ghost" size="sm" onClick={handleCancelPayment}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="mb-6 p-4 bg-primary/5 rounded-lg">
                            <div className="flex justify-between items-center mb-2">
                              <h3 className="font-medium">{selectedProduct.title}</h3>
                              <Badge variant="outline" className="ml-2">
                                {selectedProduct.quantity} {t('shop.tickets')}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground mb-4">{selectedProduct.description}</p>
                            <div className="flex justify-between text-xs">
                              <span>{t('shop.Subtotal')}</span>
                              <span>
                                {selectedProduct.price} EUR / {t('shop.ticket')}
                              </span>
                            </div>
                            <div className="flex justify-between font-medium">
                              <span>{t('payment.total')}</span>
                              <span>{selectedProduct.price * selectedProduct.quantity} EUR</span>
                            </div>
                          </div>
                          <Elements stripe={stripePromise} options={options}>
                            <CheckoutForm
                              productId={selectedProduct.id}
                              onPaymentSuccess={handlePaymentSuccess}
                              onPaymentError={handlePaymentError}
                              setInvoiceId={setInvoiceId}
                            />
                          </Elements>
                        </CardContent>
                        <div className="p-4 bg-gray-50 border-t flex items-center justify-center text-sm text-muted-foreground">
                          <Lock className="h-4 w-4 mr-2" />
                          {t('payment.secure_encryption')}
                        </div>
                      </Card>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {products.map((product) => (
                        <Card key={product.id} className="overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full">
                          <CardHeader className="bg-primary/5 pb-3">
                            <div className="flex justify-between items-start">
                              <CardTitle
                                className="text-lg"
                                style={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  width: '100%'
                                }}
                              >
                                {product.title}
                              </CardTitle>
                              <Badge variant="outline" className="flex-shrink-0 ml-2">
                                {product.quantity} {t('shop.tickets')}
                              </Badge>
                            </div>
                              <CardDescription style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                width: '100%'
                              }}>
                                {product.description}
                              </CardDescription>
                          </CardHeader>
                          <CardContent className="pt-6 flex-grow">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center">
                                <Ticket className="h-5 w-5 text-primary mr-2" />
                                <span className="text-sm whitespace-nowrap">
                                  {t('shop.pricePerTicket')}: {product.price} EUR
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center">
                                <Tag className="h-5 w-5 text-primary mr-2" />
                                <span className="text-sm whitespace-nowrap">
                                  {t('shop.totalPrice')}: {product.price * product.quantity} EUR
                                </span>
                              </div>
                            </div>
                          </CardContent>
                          <CardFooter className="bg-gray-50 border-t pt-4">
                            <Button className="w-full" onClick={() => handleBuyNow(product)}>
                              {buyButtonLoading[product.id] ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                t('shop.buyNow')
                              )}
                            </Button>
                          </CardFooter>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </CardHeader>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

interface CheckoutFormProps {
  productId: string;
  onPaymentSuccess: () => void;
  onPaymentError: (message: string) => void;
  setInvoiceId: React.Dispatch<React.SetStateAction<number | null>>;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({ productId, onPaymentSuccess, onPaymentError, setInvoiceId }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setIsProcessing(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message || 'An unexpected error occurred');
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        elements,
      });
      if (error) {
        throw new Error(error.message || 'Failed to create payment method');
      }

      const response = await fetch(`${BASE_URL}/api/shop/purchases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          productId,
        }),
      });
      const result = await response.json();
      console.log(result);
      const newInvoiceId = result.invoiceId;
      if (!response.ok) {
        throw new Error(result.message || 'Payment failed');
      }

      if (result.status === 'requires_action') {
        const { error: confirmError } = await stripe.confirmCardPayment(result.clientSecret);
        if (confirmError) {
          throw new Error(confirmError.message || 'Payment confirmation failed');
        } else {
          setInvoiceId(newInvoiceId);
          onPaymentSuccess();
        }
      } else {
        setInvoiceId(newInvoiceId);
        onPaymentSuccess();

      }
    } catch (err) {
      console.error('Payment error:', err);
      onPaymentError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      <Button type="submit" className="w-full mt-6" disabled={!stripe || !elements || isProcessing}>
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('payment.processing')}
          </>
        ) : (
          t('payment.complete_payment')
        )}
      </Button>
    </form>
  );
};

export default ShopPage;
